import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './shared/Modal';
import Spinner from './shared/Spinner';

/**
 * Simple confirmation dialog component
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {function} onClose - Function to call when dialog is closed/cancelled
 * @param {function} onConfirm - Function to call when user confirms
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message/question
 * @param {string} confirmText - Text for confirm button (default: "Confirm")
 * @param {string} confirmVariant - Variant for confirm button: "danger" or "primary" (default: "danger")
 * @param {string} cancelText - Text for cancel button (default: "Cancel")
 *
 * Async-mutation support (all optional; omit them all for the classic
 * confirm-then-close behavior):
 * @param {boolean} [pending] - When PASSED (even as false), the caller owns the
 *   lifecycle: the dialog no longer auto-closes after onConfirm (close it
 *   yourself on success), and while true the buttons disable, the confirm
 *   button shows a spinner, and backdrop/cancel dismissal is blocked.
 * @param {string} [error] - Inline error line shown under the message (e.g. a
 *   failed mutation's message) so the dialog can stay open on failure.
 * @param {string} [confirmLabelPending] - Confirm button label while pending
 *   (falls back to confirmText).
 * @param {React.ReactNode} [children] - Optional extra content rendered BELOW
 *   the message and outside its <p> (so block elements are legal). Used for
 *   opt-in controls that belong to the decision itself — e.g. the archive
 *   dialog's "Also make it private" checkbox.
 */
function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  confirmVariant = 'danger',
  cancelText,
  pending,
  error,
  confirmLabelPending,
  children
}) {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t('confirmDialog.titleDefault');
  const resolvedConfirmText = confirmText ?? t('confirmDialog.confirmTextDefault');
  const resolvedCancelText = cancelText ?? t('confirmDialog.cancelTextDefault');

  // Passing `pending` at all (even false) signals a caller-managed lifecycle:
  // onConfirm kicks off an async mutation and the caller closes on success.
  const callerManaged = pending !== undefined;
  const isPending = !!pending;

  const handleClose = () => {
    if (!isPending) onClose();
  };

  const handleConfirm = () => {
    onConfirm();
    if (!callerManaged) onClose();
  };

  const confirmButtonClass = confirmVariant === 'danger'
    ? 'bg-error text-content-inverse hover:bg-error-dark hover:shadow-elevation-2'
    : 'bg-primary text-content-inverse hover:bg-primary-dark hover:shadow-elevation-2';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="sm" position="top" ariaLabel={resolvedTitle}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-border">
        <h3 className="text-lg font-semibold text-content-primary">{resolvedTitle}</h3>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        <p className="text-content-secondary">{message}</p>
        {children && <div className="mt-3">{children}</div>}
        {error && (
          <p role="alert" className="mt-3 text-sm text-error">{error}</p>
        )}
      </div>

      {/* Footer */}
      <Modal.Footer className="rounded-b-xl flex space-x-3">
        <button
          onClick={handleClose}
          disabled={isPending}
          className="flex-1 border border-border text-content-primary py-2 px-4 rounded-lg hover:bg-background transition-colors font-medium text-sm disabled:opacity-50"
        >
          {resolvedCancelText}
        </button>
        <button
          onClick={handleConfirm}
          disabled={isPending}
          className={`flex-1 py-2 px-4 rounded-lg transition-all font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2 ${confirmButtonClass}`}
        >
          {isPending && <Spinner size="sm" variant="inverse" />}
          {isPending ? (confirmLabelPending ?? resolvedConfirmText) : resolvedConfirmText}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default ConfirmDialog;
